import { bringDetachedPopupToFront } from "./detachedPopupActivation"
import type { TooltipRegistryKey } from "@shared/components/tooltip/tooltipRegistry"

export type DetachedWindowId =
  | "crm"
  | "manual"
  | "message"
  | "uninsured"
  | "claim"
  | "purchase"
  | "inquiry"
  | "digitalSignature"
  | "waitingStatus"
  | "treatmentRoom"
  | "imagePreview"
  | "penChart"
  | "reservation"
  | "patientAnalysis"
  | "doctalkSettings"

export interface DetachedWindowEntry {
  id: string
  type: DetachedWindowId
  label: string
  title: string
  tooltipKey: TooltipRegistryKey
  focus: () => Promise<boolean>
}

const WINDOW_CONFIG: Record<DetachedWindowId, Omit<DetachedWindowEntry, "id" | "type" | "focus">> = {
  crm: { label: "CRM", title: "CRM", tooltipKey: "top.detachedCrm" },
  manual: { label: "매뉴얼", title: "매뉴얼", tooltipKey: "top.detachedManual" },
  message: { label: "메시지", title: "메시지", tooltipKey: "top.detachedMessage" },
  inquiry: { label: "문의", title: "문의하기", tooltipKey: "top.detachedInquiry" },
  digitalSignature: { label: "서명", title: "전자서명", tooltipKey: "top.detachedDigitalSignature" },
  uninsured: { label: "비급여", title: "비급여/한약", tooltipKey: "top.detachedUninsured" },
  reservation: { label: "예약", title: "리뉴얼 예약", tooltipKey: "top.detachedReservation" },
  claim: { label: "청구", title: "청구", tooltipKey: "top.detachedClaim" },
  purchase: { label: "구매", title: "구매", tooltipKey: "top.detachedPurchase" },
  waitingStatus: { label: "대기", title: "대기환자 현황", tooltipKey: "top.detachedWaitingStatus" },
  treatmentRoom: { label: "치료실", title: "치료실 현황", tooltipKey: "top.detachedTreatmentRoom" },
  imagePreview: { label: "이미", title: "환자 이미지 미리보기", tooltipKey: "top.detachedImagePreview" },
  penChart: { label: "펜차", title: "펜차트 편집", tooltipKey: "top.detachedPenChart" },
  patientAnalysis: { label: "분석", title: "환자 분석", tooltipKey: "top.detachedPatientAnalysis" },
  doctalkSettings: { label: "닥톡", title: "닥톡 환경설정", tooltipKey: "top.detachedDoctalkSettings" },
}

export const getDetachedWindowLabel = (type: DetachedWindowId) => WINDOW_CONFIG[type].label

const CLOSE_WATCH_INTERVAL_MS = 500

interface RegisteredWindow {
  type: DetachedWindowId
  popupWindow: Window
  activationTitle: string
  label: string
  cleanup: () => void
}

const registeredWindows = new Map<string, RegisteredWindow>()
const listeners = new Set<() => void>()
let snapshot: DetachedWindowEntry[] = []

const updateSnapshot = () => {
  snapshot = Array.from(registeredWindows.entries()).flatMap(([id, registered]) => {
    if (registered.popupWindow.closed) return []
    const config = WINDOW_CONFIG[registered.type]
    return [
      {
        id,
        type: registered.type,
        ...config,
        label: registered.label,
        title: registered.activationTitle,
        focus: () => bringDetachedPopupToFront(registered.popupWindow, registered.activationTitle),
      },
    ]
  })
  listeners.forEach(listener => listener())
}

export const registerDetachedWindow = (
  type: DetachedWindowId,
  popupWindow: Window,
  activationTitle = WINDOW_CONFIG[type].title,
  instanceId: string = type,
  label = WINDOW_CONFIG[type].label
) => {
  if (popupWindow.closed) return

  const existing = registeredWindows.get(instanceId)
  if (existing?.popupWindow === popupWindow) {
    existing.activationTitle = activationTitle
    existing.label = label
    updateSnapshot()
    return
  }

  registeredWindows.get(instanceId)?.cleanup()

  const unregister = () => {
    const registered = registeredWindows.get(instanceId)
    if (!registered || registered.popupWindow !== popupWindow) return
    window.clearInterval(closeWatchTimer)
    registeredWindows.delete(instanceId)
    updateSnapshot()
  }

  const closeWatchTimer = window.setInterval(() => {
    if (popupWindow.closed) unregister()
  }, CLOSE_WATCH_INTERVAL_MS)

  registeredWindows.set(instanceId, { type, popupWindow, activationTitle, label, cleanup: unregister })
  updateSnapshot()
}

export const subscribeDetachedWindows = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const getDetachedWindowsSnapshot = () => snapshot

export const clearDetachedWindowRegistry = () => {
  Array.from(registeredWindows.values()).forEach(registered => registered.cleanup())
}
